/// <amd-module name="Feed/Legacy/Actions/FeedList/FeedManager" />
import { IModuleStore } from "redux-dynamic-modules";

import { findIndex } from "OfficeFabric/Utilities";

import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IReduxStoreService } from "VSS/Redux/StoreService";

import { IFilter } from "VSSUI/Utilities/Filter";

import { FeedPermission, FeedView, Package, UpstreamSource } from "Feed/Common/Contracts/Feed";
import { CustomSet } from "Feed/Common/Types/CustomSet";
import { FeedHubActions } from "Feed/Core/Redux/FeedHub";
import { NavigationHandler } from "Feed/Legacy/Common/NavigationHandler";
import { IFeedDataService } from "Feed/Legacy/DataServices/FeedsDataService";
import { FilterByEnabledProtocolDataService } from "Feed/Legacy/DataServices/FilterByEnabledProtocolDataService";
import { IFeedSettingsDataService } from "Feed/Legacy/DataServices/SettingsDataService";
import { getFullyQualifiedFeedId, getFullyQualifiedFeedName } from "Feed/Legacy/Helpers/FeedNameResolver";
import { PackageFilterBarHelper } from "Feed/Legacy/Helpers/PackageFilterBarHelper";
import { hasAccessToBaseFeed, isDeleted, isFeedReader } from "Feed/Legacy/Helpers/PermissionHelper";
import { FeedState } from "Feed/Legacy/Types/FeedState";
import { IHubState } from "Feed/Legacy/Types/IHubState";
import { urlSourceToUpstreamSource } from "Feed/Legacy/Utils/Upstream";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

export class FeedManager {
    private pageContext: IVssPageContext;
    private state: () => FeedState;
    private feedsDataService: IFeedDataService;
    private packagesDataService: FilterByEnabledProtocolDataService;

    private _reduxStore: IModuleStore<any>;

    // set of guids for which extended feed data & permissions have been loaded
    private feedsLoaded: CustomSet<string>;

    constructor(pageContext: IVssPageContext, state: () => FeedState) {
        this.pageContext = pageContext;
        this.state = state;
        this.feedsDataService = this.pageContext.getService<IFeedDataService>("IFeedDataService");
        this.packagesDataService = this.pageContext.getService<FilterByEnabledProtocolDataService>(
            "IFeedFilterByEnabledProtocolDataService"
        );

        const storeService = this.pageContext.getService<IReduxStoreService>("redux-store-service");
        this._reduxStore = storeService.getStore(null);

        this.feedsLoaded = new CustomSet<string>();
    }

    public async setFeed(feed: Feed): Promise<void> {
        this.clear();
        if (feed && feed.deletedDate != null) {
            feed = await this.updateDeletedFeedAsync(feed);
        } else {
            feed = await this.updateFeedAsync(feed);
            await this.updateViewsAsync(feed);
            await this.updatePackagesAsync(feed);
        }

        this.feed = feed;
        this._reduxStore.dispatch(FeedHubActions.feedSelected(feed.fullyQualifiedId));

        await this.updateMruAsync(feed);
    }

    public renderNewFeed(feed: Feed): void {
        this.clear();
        this.feed = feed;
        this.packages = [];

        this.updateViewsAsync(feed);
        NavigationHandler.renderFeed(this.pageContext, feed);
    }

    /** use the hub state to determine which feed to load */
    public async setFeedFromUrlAsync(hubState: IHubState): Promise<void> {
        this.clear();

        let feedInUrl: string = null;
        let viewInUrl: string = null;

        const parts = hubState.feed.split("@");
        feedInUrl = parts[0];
        if (parts.length === 2) {
            viewInUrl = parts[1];
        }

        const feedIndex = this.findFeedIndex(feedInUrl);
        let feedToSet: Feed = null;
        if (feedIndex == -1) {
            feedToSet = await this.getDeletedFeedFromNameOrId(feedInUrl);
            feedToSet = await this.updateDeletedFeedAsync(feedToSet);
        }
        // If feed not found in deleted feed list, check the active feed list
        if (feedToSet == null) {
            feedToSet = await this.updateFeedAtIndexAsync(feedIndex);

            feedToSet.upstreamSource = this.getUpstreamSource(hubState, feedToSet);
            feedToSet.view = await this.updateViewsAndGetMatchingViewAsync(feedToSet, viewInUrl);
            await this.updatePackagesAsync(feedToSet);
        }

        this.feed = feedToSet;
        await this.updateMruAsync(feedToSet);
    }

    private async updateFeedAsync(feed: Feed): Promise<Feed> {
        if (!this.feedsLoaded.has(feed.id)) {
            const index = this.findFeedIndex(feed.name);
            return this.updateFeedAtIndexAsync(index);
        }

        return feed;
    }

    private async updateDeletedFeedAsync(deletedFeed: Feed): Promise<Feed> {
        if (deletedFeed && !this.feedState.feedUser.isAnonymousOrPublic) {
            deletedFeed.permissions = await this.getFeedPermissionsAsync(deletedFeed);
        }
        return deletedFeed;
    }

    private async getDeletedFeedFromNameOrId(feedNameOrId: string): Promise<Feed> {
        let feedToSet;
        const feeds = await this.feedsDataService.getDeletedFeedsAsync();
        feeds.forEach(feed => {
            if (
                Utils_String.equals(feed.name, feedNameOrId, true) ||
                Utils_String.equals(feed.id, feedNameOrId, true)
            ) {
                feedToSet = feed;
            }
        });
        return feedToSet;
    }

    private async updateFeedAtIndexAsync(index: number): Promise<Feed> {
        if (index === -1) {
            index = 0;
        }
        let feed = this.feedState.feeds[index];
        if (!this.feedsLoaded.has(feed.id)) {
            feed = await this.getFeedAsync(feed);
            if (!this.feedState.feedUser.isAnonymousOrPublic) {
                feed.permissions = await this.getFeedPermissionsAsync(feed);
            }

            this.feedsLoaded.add(feed.id);
            this.feedState.feeds[index] = feed;
        }
        return feed;
    }

    public async updateViewsAndGetMatchingViewAsync(feedToSet: Feed, viewInUrl: string): Promise<FeedView> {
        await this.updateViewsAsync(feedToSet);

        const viewIndex = findIndex(this.views, (view: FeedView) => {
            return Utils_String.equals(view.name, viewInUrl, true) || Utils_String.equals(view.id, viewInUrl, true);
        });
        if (viewIndex === -1) {
            return null;
        } else {
            return this.views[viewIndex];
        }
    }

    private findFeedIndex(feedInUrl: string): number {
        const index = findIndex(this.feedState.feeds, (feed: Feed) => {
            return Utils_String.equals(feed.name, feedInUrl, true) || Utils_String.equals(feed.id, feedInUrl, true);
        });

        return index;
    }

    private async updateViewsAsync(feed: Feed): Promise<void> {
        this.views = await this.getViews(feed);

        // there are no views or user doesn't have access to any view
        if (this.views.length === 0) {
            feed.view = null;
            return;
        }

        if (hasAccessToBaseFeed(feed) === false || isFeedReader(feed) === true) {
            const view = this.getAccessibleViewEitherDefaultOrFirst(feed, this.views);
            feed.view = view;
            const currentFilterState = this.feedState.hubState.filter.getState();

            PackageFilterBarHelper.setFeedViewToFilterState(currentFilterState, view);

            feed.fullyQualifiedId = getFullyQualifiedFeedId(feed);
            feed.fullyQualifiedName = getFullyQualifiedFeedName(feed);
        }
    }

    private getUpstreamSource(hubState: IHubState, feedToSet: Feed): UpstreamSource {
        if (hubState.upstreamSource) {
            return urlSourceToUpstreamSource(hubState, this.feed.upstreamSources);
        }
        return null;
    }

    /**
     * Pick feed's default view if available to user
     * or top view user has access to
     */
    private getAccessibleViewEitherDefaultOrFirst(feed: Feed, views: FeedView[]): FeedView {
        // user doesn't have access to any views
        if (views.length === 0) {
            return null;
        }

        // there is no default view set for this feed
        if (feed.defaultViewId == null) {
            return views[0];
        }

        const index = findIndex(views, (view: FeedView) => {
            return view.id === feed.defaultViewId;
        });

        // user has access to defaultView
        if (index >= 0) {
            return views[index];
        }

        // user did not have access to default view
        return views[0];
    }

    private clear(): void {
        this.clearFilters();
        this.clearPackages();
        this.clearViews();
        this.clearSelectedUpstreamSource();
    }

    private clearPackages(): void {
        this.packages = [];
        this.feedState.selectedPackages = [];
    }

    private clearViews(): void {
        this.views = [];
        this.feedsView = null;
    }

    private clearSelectedUpstreamSource(): void {
        this.feedUpstreamSource = null;
    }

    private async updatePackagesAsync(feed: Feed): Promise<void> {
        const includeDeleted = isDeleted(feed) !== false;
        const packages = await this.packagesDataService.getPackagesAsync(
            feed,
            this.feedState.pageSize, // top
            null, // skip - default 0
            includeDeleted, // default false
            null, // isListed
            null, // packageNameQuery
            true // includeDescription
        );
        this.feedState.requestedPackageCount = this.feedState.pageSize;

        this.packages = packages;
    }

    private async getFeedAsync(feed: Feed): Promise<Feed> {
        const projectId = feed.project ? feed.project.id : null;
        const refreshedFeed = await this.feedsDataService.getFeedAsync(projectId, feed.id);
        this._reduxStore.dispatch(FeedHubActions.getFeedCompleted(feed));
        return refreshedFeed as Feed;
    }

    private async getFeedPermissionsAsync(feed: Feed): Promise<FeedPermission[]> {
        const identityDescriptors =
            this.feedState && this.feedState.feedUser ? this.feedState.feedUser.identityDescriptors : null;
        const feedPermissions: FeedPermission[] = await this.feedsDataService.getFeedPermissionsAsync(
            feed,
            false /*includeIds*/,
            false /*excludeInheritedPermissions*/,
            identityDescriptors,
            !!feed.deletedDate
        );
        this._reduxStore.dispatch(FeedHubActions.getFeedPermissionsCompleted(feed.id, feedPermissions));
        return feedPermissions;
    }

    private async getViews(feed: Feed): Promise<FeedView[]> {
        const projectId = feed.project ? feed.project.id : null;
        const feedViews: FeedView[] = await this.feedsDataService.getFeedViewsAsync(projectId, feed.id);
        this._reduxStore.dispatch(FeedHubActions.getFeedViewsCompleted(feed.id, feedViews));
        return feedViews;
    }

    private clearFilters(): void {
        this.feedState.hubState.filter.setState({}, true /*suppressChangeEvent*/);
    }

    private async updateMruAsync(feed: Feed): Promise<void> {
        if (!this.feedState.feedUser.isAnonymousOrPublic) {
            // Set mru as feedId@viewId is view exists
            const feedId = getFullyQualifiedFeedId(feed);
            const settingsDataService = this.pageContext.getService<IFeedSettingsDataService>(
                "IFeedSettingsDataService"
            );
            await settingsDataService.setMruFullyQualifiedFeedId(feedId);
        }
    }

    get feedState(): FeedState {
        return this.state();
    }

    get feedId(): string {
        // when a new feed is created for the first time, selectedFeed is null
        if (this.feedState.selectedFeed == null) {
            return null;
        }

        return this.feedState.selectedFeed.id;
    }

    set feedId(id: string) {
        this.feedState.selectedFeed.id = id;
    }

    get feed(): Feed {
        return this.feedState.selectedFeed;
    }

    set feed(feed: Feed) {
        this.feedState.selectedFeed = feed;
    }

    set packages(packages: Package[]) {
        this.feedState.packages = packages;
    }

    get views(): FeedView[] {
        return this.feedState.feedViews;
    }

    set views(views: FeedView[]) {
        this.feedState.feedViews = views;
    }

    set feedsView(view: FeedView) {
        // when first feed is getting created, selectedFeed is null
        if (this.feedState.selectedFeed == null) {
            return;
        }
        this.feedState.selectedFeed.view = view;
    }

    set feedUpstreamSource(upstreamSource: UpstreamSource) {
        // when first feed is getting created, selectedFeed is null
        if (this.feedState.selectedFeed == null) {
            return;
        }
        this.feedState.selectedFeed.upstreamSource = upstreamSource;
    }

    get filter(): IFilter {
        return this.feedState.hubState.filter;
    }
}
